import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {

  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  Paper,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  PlayCircleOutline,
  Assignment,
  Quiz,
  CheckCircle,
  Close,
  Send,
  Refresh,
  ExpandMore,
  VideoLibrary,
  Description,
  Link as LinkIcon,
  PictureAsPdf,
  YouTube,
  School,
  LightbulbOutlined,
  PsychologyOutlined,
  FlashOnOutlined,
  Star,
  BookmarkBorder,
  Fullscreen,
  FullscreenExit,
  AutoAwesome,
} from '@mui/icons-material';
// import { generateTopicContent } from '../services/adminTopicService';
import { getCoreTopicById, getCurriculumSubjectById } from '../services/adminCourseService';
import { getTopicDetails as getStudentTopicDetails } from '../services/curriculumService';
import { getStudentTopicContent } from '../services/topicContentService';
import courseService from '../services/courseService';
import PracticeQuizCard from '../components/Quiz/PracticeQuizCard';
import AssessmentCard from '../components/Assessment/AssessmentCard';
import ActivityFeed from '../components/Activity/ActivityFeed';
import activityService from '../services/activityService';

// Markdown Dependencies
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css'; // Import KaTeX CSS


interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  userType: string;
}

interface Topic {
  id: number;
  name: string;
  description: string;
  status: 'not_started' | 'in_progress' | 'completed';
  estimatedTime: string;
  currentStep: number;
  learningContent: {
    introduction: string;
    videoUrl?: string;
    videoDuration?: string;
  };
  practiceProblems: PracticeProblem[];
  knowledgeCheck: QuizQuestion[];
  supportingResources: SupportingResource[];
}

interface PracticeProblem {
  id: number;
  question: string;
  type: 'text' | 'multiple_choice';
  options?: string[];
  hint?: string;
  solution: string;
  explanation: string;
}

interface QuizQuestion {
  id: number;
  question: string;
  type: 'multiple_choice' | 'true_false' | 'text';
  options?: string[];
  correctAnswer: string;
  explanation?: string;
}

interface SupportingResource {
  id: number;
  title: string;
  type: 'youtube' | 'pdf' | 'link';
  url: string;
  description: string;
  duration?: string;
}

// Simple Error Boundary Component
class MarkdownErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("Markdown Rendering Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to render content. Please contact support.
        </Alert>
      );
    }
    return this.props.children;
  }
}

const TopicDetails: React.FC<{ preview?: boolean }> = ({ preview = false }) => {
  const { courseId, subjectId, topicId } = useParams<{ courseId: string; subjectId: string; topicId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [topic, setTopic] = useState<Topic | null>(null);
  const [subjectName, setSubjectName] = useState<string>('');
  const [courseName, setCourseName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0); // 0: Learn, 1: Practice, 2: Knowledge Check
  const [isLearningMode, setIsLearningMode] = useState(false); // Hide distractions when true
  const [practiceAnswers, setPracticeAnswers] = useState<{ [key: number]: string }>({});
  const [quizAnswers, setQuizAnswers] = useState<{ [key: number]: string }>({});
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [quizResults, setQuizResults] = useState<any>(null);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [showSupportingResource, setShowSupportingResource] = useState<SupportingResource | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Helper to handle AI generation
  const handleGenerateContent = async () => {
    if (!topicId || !subjectId) return;
    setIsGenerating(true);
    try {
      /*
      const content = await generateTopicContent(
        parseInt(subjectId),
        parseInt(topicId),
        {
          topicTitle: 'Linear Equations', 
          difficultyLevel: 'Beginner',
          contentType: 'Lesson'
        }
      );
      // For verification, just update the intro
      if (topic) {
        setTopic({
          ...topic,
          learningContent: {
            ...topic.learningContent,
            introduction: content
          }
        });
      }
      */
      alert('Content generated successfully!');
    } catch (error) {
      console.error('Failed to generate content:', error);
      alert('Failed to generate content. Check console.');
    } finally {
      setIsGenerating(false);
    }
  };



  useEffect(() => {
    // Get user data from localStorage
    if (preview) {
      setUser({
        id: 0,
        email: 'admin@preview.com',
        firstName: 'Preview',
        lastName: 'Admin',
        userType: 'SYSTEM_ADMIN'
      });
    } else {
      const userData = localStorage.getItem('user');
      if (userData) {
        setUser(JSON.parse(userData));
      }
    }

    const fetchTopicData = async () => {
      try {
        setLoading(true);
        if (!topicId) return;

        // Record topic view activity
        if (courseId && topicId) {
          try {
            await activityService.recordTopicView(
              parseInt(topicId),
              parseInt(courseId)
            );
          } catch (error) {
            console.error('Failed to record topic view:', error);
          }
        }

        const topicIdNum = parseInt(topicId);
        const courseIdNum = courseId ? parseInt(courseId) : undefined;

        let topicTitle: string;
        let topicDescription: string;
        let topicStatus: Topic['status'] = 'in_progress';
        let estimatedMinutes = 30;
        let legacyInlineContent: string | undefined;

        if (preview) {
          const coreTopic = await getCoreTopicById(topicIdNum);
          topicTitle = coreTopic.title;
          topicDescription = coreTopic.description || 'No description available';
          legacyInlineContent = coreTopic.content;
        } else {
          const topicDto = await getStudentTopicDetails(topicIdNum, courseIdNum);
          topicTitle = topicDto.title;
          topicDescription = topicDto.description || 'No description available';
          estimatedMinutes = topicDto.estimatedTimeMinutes || 30;
          if (topicDto.progressStatus === 'COMPLETED') {
            topicStatus = 'completed';
          } else if (topicDto.progressStatus === 'IN_PROGRESS' || topicDto.progressStatus === 'INPROGRESS') {
            topicStatus = 'in_progress';
          } else {
            topicStatus = 'not_started';
          }
        }

        // Fetch Course Name
        if (courseId) {
          try {
            const courseData = await courseService.getCourseById(parseInt(courseId));
            setCourseName(courseData.name);
          } catch (err) {
            console.error("Failed to fetch course details", err);
            setCourseName('Course');
          }
        }

        // Subject label: admin preview uses curriculum subject; students use course topics group name
        if (preview && subjectId) {
          try {
            const subjectData = await getCurriculumSubjectById(parseInt(subjectId));
            setSubjectName(subjectData.name);
          } catch (err) {
            console.error("Failed to fetch subject details", err);
            setSubjectName("Subject");
          }
        } else if (courseId) {
          setSubjectName('Topics');
        }

        const topicBodyMarkdown = await getStudentTopicContent(topicIdNum);

        const mappedTopic: Topic = {
          id: topicIdNum,
          name: topicTitle,
          description: topicDescription,
          status: topicStatus,
          estimatedTime: `${estimatedMinutes} mins`,
          currentStep: 0,
          learningContent: {
            introduction:
              topicBodyMarkdown ||
              legacyInlineContent ||
              '# No Content Available\n\nAsk your admin to generate content for this topic using the AI Assistant.',
            videoUrl: '',
            videoDuration: ''
          },
          practiceProblems: [],
          knowledgeCheck: [],
          supportingResources: []
        };

        setTopic(mappedTopic);
        setCurrentStep(0);
      } catch (error) {
        console.error('Failed to fetch topic details:', error);
        // Fallback or error state?
        // For now, let's show an error alert via state if needed, but existing code handles null topic
      } finally {
        setLoading(false);
      }
    };

    fetchTopicData();
  }, [courseId, subjectId, topicId]);

  const handleBackToSubject = () => {
    if (preview) {
      navigate(`/admin/preview/courses/${courseId}`);
    } else {
      navigate(`/student-courses/${courseId}`);
    }
  };

  const handleStepChange = (step: number) => {
    setCurrentStep(step);
    setIsLearningMode(false); // Exit learning mode when switching steps
  };

  const handleStartLearning = () => {
    setIsLearningMode(true);
  };

  const handleExitLearningMode = () => {
    setIsLearningMode(false);
  };

  const handleWatchVideo = () => {
    setShowVideoPlayer(true);
  };

  const handlePracticeAnswerChange = (problemId: number, answer: string) => {
    setPracticeAnswers(prev => ({
      ...prev,
      [problemId]: answer
    }));
  };

  const handleQuizAnswerChange = (questionId: number, answer: string) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleQuizSubmit = () => {
    if (!topic) return;

    const results = {
      totalQuestions: topic.knowledgeCheck.length,
      correctAnswers: 0,
      questions: topic.knowledgeCheck.map(question => {
        const userAnswer = quizAnswers[question.id];
        const isCorrect = userAnswer?.toLowerCase().trim() === question.correctAnswer?.toLowerCase().trim();

        if (isCorrect) results.correctAnswers++;

        return {
          id: question.id,
          question: question.question,
          userAnswer: userAnswer || 'No answer provided',
          correctAnswer: question.correctAnswer,
          isCorrect,
          explanation: question.explanation
        };
      }),
      score: 0,
      submittedAt: new Date().toISOString()
    };

    results.score = results.totalQuestions > 0
      ? Math.round((results.correctAnswers / results.totalQuestions) * 100)
      : 0;
    setQuizResults(results);
    setShowQuizResults(true);
  };

  const handleSupportingResourceClick = (resource: SupportingResource) => {
    setShowSupportingResource(resource);
  };

  const getStepIcon = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return <LightbulbOutlined />;
      case 1:
        return <PsychologyOutlined />;
      case 2:
        return <FlashOnOutlined />;
      default:
        return <School />;
    }
  };

  const getStepTitle = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return 'Learn';
      case 1:
        return 'Practice';
      case 2:
        return 'Knowledge Check';
      default:
        return 'Step';
    }
  };

  const renderLearningContent = () => {
    if (!topic) return null;

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            📚 Learn: {topic.name}
          </Typography>
          {isLearningMode && (
            <IconButton onClick={handleExitLearningMode} color="primary">
              <FullscreenExit />
            </IconButton>
          )}
          {!isLearningMode && (
            <Tooltip title="Feature is provided for paid courses">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesome />}
                  disabled
                >
                  AI → Learn With AI
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            {/* Video Section */}
            {topic.learningContent.videoUrl && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <VideoLibrary color="primary" />
                  <Box>
                    <Typography variant="h6">Introduction Video</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Duration: {topic.learningContent.videoDuration}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<PlayCircleOutline />}
                    onClick={handleWatchVideo}
                    sx={{ ml: 'auto' }}
                  >
                    Watch Video
                  </Button>
                </Box>
                <Divider />
              </Box>
            )}

            {/* Learning Content */}
            <Box
              sx={{
                '& h1': { fontSize: '1.75rem', fontWeight: 600, mb: 2, mt: 3, color: 'primary.main' },
                '& h2': { fontSize: '1.4rem', fontWeight: 600, mb: 1.5, mt: 2.5, borderBottom: '1px solid', borderColor: 'divider', pb: 1 },
                '& h3': { fontSize: '1.15rem', fontWeight: 600, mb: 1, mt: 2 },
                '& p': { mb: 1.5, lineHeight: 1.7 },
                '& ul, & ol': { pl: 3, mb: 2 },
                '& li': { mb: 0.5 },
                '& blockquote': {
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                  m: 0,
                  p: 2,
                  mb: 2,
                  borderRadius: 1,
                  fontStyle: 'italic'
                },
                '& table': {
                  borderCollapse: 'collapse',
                  width: '100%',
                  mb: 3,
                  mt: 2
                },
                '& th': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  p: 1.5,
                  textAlign: 'left',
                  fontWeight: 600
                },
                '& td': {
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 1.5
                },
                '& tr:nth-of-type(even)': {
                  bgcolor: 'action.hover'
                },
                '& pre': {
                  bgcolor: 'grey.900',
                  color: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  mb: 2,
                  fontFamily: 'monospace'
                },
                '& code': {
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  bgcolor: 'action.hover',
                  p: 0.5,
                  borderRadius: 1
                }
              }}
            >
              <MarkdownErrorBoundary>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkMath]}
                  rehypePlugins={[rehypeKatex]}
                  components={{
                    blockquote: ({ node, ...props }) => {
                      return <Box component="blockquote" {...props} />;
                    },
                    a: ({ node, ...props }) => <Link {...props} target="_blank" rel="noopener noreferrer" />,
                    table: ({ node, ...props }) => (
                      <Box sx={{ overflowX: 'auto', mb: 2 }}>
                        <table {...props} />
                      </Box>
                    )
                  }}
                >
                  {topic.learningContent.introduction}
                </ReactMarkdown>
              </MarkdownErrorBoundary>

            </Box>

            {!isLearningMode && (
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Fullscreen />}
                  onClick={handleStartLearning}
                >
                  Focus Mode
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => handleStepChange(1)}
                  disabled={currentStep < 0}
                >
                  Continue to Practice
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box >
    );
  };

  const renderPracticeProblems = () => {
    if (!topic) return null;

    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" fontWeight={600} sx={{ mb: 1 }}>
            🧠 Practice Section
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Test your understanding and reinforce your learning through practice quizzes and problems.
          </Typography>
        </Box>

        {/* Practice Quiz Section */}
        <Box sx={{ mb: 4 }}>
          {topicId && subjectId && (
            <PracticeQuizCard topicId={parseInt(topicId)} subjectId={parseInt(subjectId)} />
          )}
        </Box>

        {/* Additional Practice Problems */}
        {topic.practiceProblems.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" component="h3" fontWeight={600} sx={{ mb: 2 }}>
              Additional Practice Problems
            </Typography>
            {topic.practiceProblems.map((problem, index) => (
          <Card key={problem.id} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Problem {index + 1}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {problem.question}
              </Typography>

              {problem.type === 'text' && (
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Enter your answer..."
                  value={practiceAnswers[problem.id] || ''}
                  onChange={(e) => handlePracticeAnswerChange(problem.id, e.target.value)}
                  sx={{ mb: 2 }}
                />
              )}

              {problem.type === 'multiple_choice' && (
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <RadioGroup
                    value={practiceAnswers[problem.id] || ''}
                    onChange={(e) => handlePracticeAnswerChange(problem.id, e.target.value)}
                  >
                    {problem.options?.map((option, oIndex) => (
                      <FormControlLabel
                        key={oIndex}
                        value={option}
                        control={<Radio />}
                        label={option}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}

              {problem.hint && (
                <Accordion sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="body2" color="text.secondary">
                      💡 Need a hint?
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2">
                      {problem.hint}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="body2" color="primary">
                    📖 View Solution & Explanation
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography variant="subtitle2" color="success.main" gutterBottom>
                      Solution: {problem.solution}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {problem.explanation}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </CardContent>
            </Card>
          ))}
          </Box>
        )}

        {topic.practiceProblems.length === 0 && (
          <Card variant="outlined" sx={{ bgcolor: 'grey.50', py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No additional practice problems available at this time.
            </Typography>
          </Card>
        )}

        <Box sx={{ display: 'flex', gap: 2, mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="outlined"
            onClick={() => handleStepChange(0)}
            startIcon={<ArrowBack />}
          >
            Back to Learn
          </Button>
          <Button
            variant="contained"
            onClick={() => handleStepChange(2)}
            sx={{ ml: 'auto' }}
          >
            Take Knowledge Check
          </Button>
        </Box>
      </Box>
    );
  };

  const renderKnowledgeCheck = () => {
    if (!topic) return null;

    return (
      <Box>
        <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
          ⚡ Knowledge Check
        </Typography>

        {/* Assessments Section */}
        {courseId && (
          <Box sx={{ mb: 4 }}>
            <AssessmentCard 
              courseId={parseInt(courseId)} 
              topicIds={topicId ? [parseInt(topicId)] : undefined}
            />
          </Box>
        )}

        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Test your understanding with these questions. Take your time and show what you've learned!
            </Typography>

            {topic.knowledgeCheck.map((question, index) => (
              <Box key={question.id} sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  {index + 1}. {question.question}
                </Typography>

                {question.type === 'multiple_choice' && (
                  <FormControl component="fieldset">
                    <RadioGroup
                      value={quizAnswers[question.id] || ''}
                      onChange={(e) => handleQuizAnswerChange(question.id, e.target.value)}
                    >
                      {question.options?.map((option, oIndex) => (
                        <FormControlLabel
                          key={oIndex}
                          value={option}
                          control={<Radio />}
                          label={option}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                )}

                {question.type === 'true_false' && (
                  <FormControl component="fieldset">
                    <RadioGroup
                      value={quizAnswers[question.id] || ''}
                      onChange={(e) => handleQuizAnswerChange(question.id, e.target.value)}
                    >
                      <FormControlLabel value="True" control={<Radio />} label="True" />
                      <FormControlLabel value="False" control={<Radio />} label="False" />
                    </RadioGroup>
                  </FormControl>
                )}

                {question.type === 'text' && (
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Enter your answer..."
                    value={quizAnswers[question.id] || ''}
                    onChange={(e) => handleQuizAnswerChange(question.id, e.target.value)}
                  />
                )}

                {index < topic.knowledgeCheck.length - 1 && <Divider sx={{ mt: 3 }} />}
              </Box>
            ))}

            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => handleStepChange(1)}
              >
                Back to Practice
              </Button>
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={handleQuizSubmit}
                size="large"
              >
                Submit Knowledge Check
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ mt: 4, px: 3, maxWidth: 'lg', mx: 'auto' }}>
        <Alert severity="error">Please log in to access topic details.</Alert>
      </Box>
    );
  }

  if (!topic) {
    return (
      <Box sx={{ mt: 4, px: 3, maxWidth: 'lg', mx: 'auto' }}>
        <Alert severity="error">Topic not found.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: isLearningMode ? 0 : 4, mb: 4, px: isLearningMode ? 0 : 3, maxWidth: isLearningMode ? '100%' : 'lg', mx: 'auto' }}>
      {!isLearningMode && (
        <>
          {/* Breadcrumb Navigation */}
          <Box sx={{ mb: 3 }}>
            <Breadcrumbs aria-label="breadcrumb">
              <Link color="inherit" href="#" onClick={() => {
                if (preview) navigate('/admin/courses');
                else navigate('/student-courses');
              }} sx={{ cursor: 'pointer' }}>
                My Courses
              </Link>
              <Link color="inherit" href="#" onClick={handleBackToSubject} sx={{ cursor: 'pointer' }}>
                {courseName || 'Course'}
              </Link>
              <Typography color="text.primary">{topic.name}</Typography>
            </Breadcrumbs>
          </Box>

          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton onClick={handleBackToSubject} sx={{ mr: 2 }}>
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h4" component="h1">
                  {topic.name}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {topic.description}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Chip label={topic.estimatedTime} color="primary" size="small" />
              <Chip label={topic.status.replace('_', ' ')} color="success" size="small" />
            </Box>
          </Box>
        </>
      )}

      <Grid container spacing={3}>
        {/* Main Learning Content */}
        <Grid item xs={12} md={isLearningMode ? 12 : 8}>
          {!isLearningMode && (
            <Box sx={{ mb: 4 }}>
              <Stepper activeStep={currentStep} alternativeLabel>
                {['Learn', 'Practice', 'Knowledge Check'].map((label, index) => (
                  <Step key={label}>
                    <StepButton onClick={() => handleStepChange(index)}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStepIcon(index)}
                        {label}
                      </Box>
                    </StepButton>
                  </Step>
                ))}
              </Stepper>
            </Box>
          )}

          {/* Step Content */}
          <Box>
            {currentStep === 0 && renderLearningContent()}
            {currentStep === 1 && renderPracticeProblems()}
            {currentStep === 2 && renderKnowledgeCheck()}
          </Box>
        </Grid>

        {/* Supporting Resources Sidebar */}
        {!isLearningMode && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📚 Supporting Resources
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Additional materials to enhance your learning
                </Typography>

                <List dense>
                  {topic.supportingResources.map((resource) => (
                    <ListItem
                      key={resource.id}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        borderRadius: 1,
                      }}
                      onClick={() => handleSupportingResourceClick(resource)}
                    >
                      <ListItemIcon>
                        {resource.type === 'youtube' && <YouTube color="error" />}
                        {resource.type === 'pdf' && <PictureAsPdf color="error" />}
                        {resource.type === 'link' && <LinkIcon color="primary" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={resource.title}
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              {resource.description}
                            </Typography>
                            {resource.duration && (
                              <Typography variant="caption" color="primary">
                                {resource.duration}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>

            {/* Progress Card */}
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📊 Progress
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Learning Steps: {currentStep + 1}/3
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={((currentStep + 1) / 3) * 100}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Video Player Modal */}
      <Dialog
        open={showVideoPlayer}
        onClose={() => setShowVideoPlayer(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Introduction Video</Typography>
          <IconButton onClick={() => setShowVideoPlayer(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
            <iframe
              src={topic?.learningContent.videoUrl}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              allowFullScreen
              title="Learning Video"
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Supporting Resource Modal */}
      {showSupportingResource && (
        <Dialog
          open={Boolean(showSupportingResource)}
          onClose={() => setShowSupportingResource(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{showSupportingResource.title}</Typography>
            <IconButton onClick={() => setShowSupportingResource(null)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {showSupportingResource.type === 'youtube' && (
              <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                <iframe
                  src={showSupportingResource.url}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  allowFullScreen
                  title={showSupportingResource.title}
                />
              </Box>
            )}
            {showSupportingResource.type === 'pdf' && (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <PictureAsPdf sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" gutterBottom>
                  PDF resources will be embedded here in the full implementation
                </Typography>
                <Button variant="contained" href={showSupportingResource.url} target="_blank">
                  Open PDF
                </Button>
              </Box>
            )}
            {showSupportingResource.type === 'link' && (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <LinkIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" gutterBottom>
                  {showSupportingResource.description}
                </Typography>
                <Button variant="contained" href={showSupportingResource.url} target="_blank">
                  Open Link
                </Button>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Quiz Results Modal */}
      {quizResults && (
        <Dialog
          open={showQuizResults}
          onClose={() => setShowQuizResults(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Knowledge Check Results</Typography>
            <IconButton onClick={() => setShowQuizResults(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
              <Typography variant="h4" color="primary.main" gutterBottom>
                {quizResults.score}%
              </Typography>
              <Typography variant="h6" gutterBottom>
                {quizResults.correctAnswers}/{quizResults.totalQuestions} Correct
              </Typography>
              <Chip
                label={quizResults.score >= 80 ? 'Excellent!' : quizResults.score >= 60 ? 'Good Work!' : 'Keep Practicing!'}
                color={quizResults.score >= 80 ? 'success' : quizResults.score >= 60 ? 'warning' : 'error'}
              />
            </Paper>

            {quizResults.questions.map((question: any, index: number) => (
              <Box key={question.id} sx={{ mb: 3, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1">
                    {index + 1}. {question.question}
                  </Typography>
                  <Chip
                    label={question.isCorrect ? 'Correct' : 'Incorrect'}
                    color={question.isCorrect ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Your Answer:</strong> {question.userAnswer}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Correct Answer:</strong> {question.correctAnswer}
                </Typography>
                {question.explanation && (
                  <Paper sx={{ p: 2, bgcolor: question.isCorrect ? 'success.50' : 'error.50', mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Explanation:</strong> {question.explanation}
                    </Typography>
                  </Paper>
                )}
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowQuizResults(false)}>Close</Button>
            <Button variant="contained" onClick={() => { setShowQuizResults(false); setQuizAnswers({}); }}>
              Retake Quiz
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default TopicDetails;